import { Address, Cell } from "@ton/core";
import {
  Factory,
  Pool,
  TonPool,
  MIN_CREATE_JETTON_POOL_FEE,
  MIN_CREATE_TON_POOL_FEE,
  TON_POOL_DENOMINATIONS,
  buildCreatePool,
  buildCreateTonPool,
  buildWithdraw,
  finalizeDeposit,
  parseNote,
  prepareDeposit,
  type BuiltMessage,
  type DepositPhase,
  type DepositPrep,
  type Groth16Proof,
  type Poseidon2,
  type Prover,
  type WithdrawPhase,
  type WithdrawPlan,
} from "@tonresistor/zkresistor-sdk";
import { CatalogClient } from "./clients.js";
import type {
  JettonMetadata,
  PoolKind,
  ProtocolApi,
  ProtocolManifest,
} from "./contracts.js";
import { ASSETS } from "./assets.js";
import type { PoolSession, PoolSessionFactory } from "./merkle.js";

export interface ProtocolPool {
  id: string;
  kind: PoolKind;
  address: string;
  symbol: string;
  name: string;
  denomination: bigint;
  decimals: number;
  decimalsVerified: boolean;
  image: string | null;
  jettonMaster: string | null;
  nextIndex: number;
  withdrawalCount: number;
  privacySet: number;
  estimatedTvlNanoGram: bigint | null;
  capacity: number;
  currentRoot: bigint;
  ready: boolean;
}

export interface ArtifactProvider {
  poseidon2(): Promise<Poseidon2>;
  insertProver: Prover;
  withdrawProver: Prover;
}

export interface WalletBroadcaster {
  currentAddress(): string | null;
  send(message: BuiltMessage): Promise<{ boc?: string }>;
}

type Listener = () => void;

abstract class HeadlessController<State> {
  private readonly listeners = new Set<Listener>();

  abstract getSnapshot(): State;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  protected emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export type CatalogState =
  | { status: "idle"; pools: ProtocolPool[] }
  | { status: "loading"; pools: ProtocolPool[] }
  | { status: "ready"; pools: ProtocolPool[]; observedAt: string }
  | { status: "error"; pools: ProtocolPool[]; error: string };

function codeHash(accountCode: string | undefined): string {
  if (accountCode === undefined) throw new Error("Active account returned no code");
  return Cell.fromBase64(accountCode).hash().toString("hex");
}

function canonicalAddress(value: string): string {
  return Address.parse(value).toString({ bounceable: true, urlSafe: true });
}

function legacyJettonIdentity(data: string): {
  factoryAddress: string;
  jettonMaster: string;
  denomination: bigint;
} {
  const main = Cell.fromBase64(data).beginParse();
  const identity = main.loadRef().beginParse();
  const jettonMaster = canonicalAddress(identity.loadAddress().toString());
  const factoryAddress = canonicalAddress(identity.loadAddress().toString());
  const denomination = identity.loadCoins();
  if (identity.remainingBits !== 0 || identity.remainingRefs !== 0) {
    throw new Error("Legacy Pool identity contains trailing data");
  }
  return { factoryAddress, jettonMaster, denomination };
}

export class ProtocolCatalogController extends HeadlessController<CatalogState> {
  private state: CatalogState = { status: "idle", pools: [] };

  constructor(
    private readonly api: ProtocolApi,
    private readonly manifest: ProtocolManifest,
  ) {
    super();
  }

  getSnapshot = (): CatalogState => this.state;

  async load(): Promise<void> {
    this.state = { status: "loading", pools: this.state.pools };
    this.emit();
    try {
      const catalog = await this.api.catalog();
      const client = new CatalogClient(catalog, this.manifest);
      if (codeHash(catalog.factory.account.code) !== this.manifest.codeHashes.factory) {
        throw new Error("Factory bytecode does not match the protocol manifest");
      }
      const storage = Factory.parseFactoryStorage(catalog.factory.account.data ?? "");
      if (
        storage.poolCodeHash !== this.manifest.codeHashes.pool ||
        storage.tonPoolCodeHash !== this.manifest.codeHashes.tonPool
      ) throw new Error("Factory embedded Pool bytecode does not match the protocol manifest");
      const metadata = new Map(catalog.pools.flatMap((pool) =>
        pool.metadata === null || pool.jettonMaster === null
          ? []
          : [[canonicalAddress(pool.jettonMaster), pool.metadata] as const]));
      const infos = await Factory.listPools(client, this.manifest.factoryAddress, {
        resolveJettonMeta: async (master) => {
          const meta = metadata.get(canonicalAddress(master));
          return meta ?? { symbol: master.slice(0, 6), decimals: 0, name: null, image: null };
        },
      });
      const accountKinds = new Map(catalog.pools.map((pool) => [canonicalAddress(pool.address), pool]));
      const canonicalPools = await Promise.all(infos.map(async (pool): Promise<ProtocolPool> => {
        const envelope = accountKinds.get(canonicalAddress(pool.poolAddress));
        if (envelope === undefined || envelope.kind !== pool.kind) {
          throw new Error("Catalog Pool account is missing or has the wrong kind");
        }
        const expected = pool.kind === "jetton"
          ? this.manifest.codeHashes.pool
          : this.manifest.codeHashes.tonPool;
        if (codeHash(envelope.account.code) !== expected) {
          throw new Error(`Pool ${pool.poolAddress} bytecode does not match the protocol manifest`);
        }
        const withdrawalCount = pool.kind === "jetton"
          ? (await Pool.readState(client, pool.poolAddress)).withdrawalCount
          : TonPool.syncTargetFromState(
              pool.poolAddress,
              await TonPool.readState(client, pool.poolAddress),
            ).withdrawalCount;
        const activeDeposits = pool.nextIndex - withdrawalCount;
        if (envelope.privacySet < activeDeposits || envelope.privacySet > pool.nextIndex) {
          throw new Error(`Pool ${pool.poolAddress} returned invalid privacy metrics`);
        }
        return {
          id: pool.poolAddress,
          kind: pool.kind,
          address: pool.poolAddress,
          symbol: pool.kind === "ton" ? "GRAM" : pool.jettonSymbol,
          name: pool.kind === "ton" ? "GRAM" : pool.jettonName ?? pool.jettonSymbol,
          denomination: pool.denomination,
          decimals: pool.kind === "ton" ? 9 : pool.jettonDecimals,
          decimalsVerified: pool.kind === "ton" || envelope.metadata?.decimalsVerified === true,
          image: pool.kind === "ton" ? ASSETS.gram : pool.jettonImage,
          jettonMaster: pool.kind === "ton" ? null : pool.jettonMaster,
          nextIndex: pool.nextIndex,
          withdrawalCount,
          privacySet: envelope.privacySet,
          estimatedTvlNanoGram: envelope.estimatedTvlNanoGram === null
            ? null
            : BigInt(envelope.estimatedTvlNanoGram),
          capacity: pool.capacity,
          currentRoot: pool.currentRoot,
          ready: pool.kind === "ton" || pool.jettonWallet !== null,
        };
      }));
      const legacyPools = await Promise.all(this.manifest.legacyPools.map(async (legacy): Promise<ProtocolPool> => {
        const envelope = accountKinds.get(canonicalAddress(legacy.address));
        if (
          envelope === undefined ||
          envelope.kind !== legacy.kind ||
          envelope.jettonMaster === null ||
          canonicalAddress(envelope.jettonMaster) !== legacy.jettonMaster
        ) throw new Error("Pinned legacy Pool account is missing or has the wrong identity");
        if (codeHash(envelope.account.code) !== this.manifest.codeHashes.pool) {
          throw new Error(`Pool ${legacy.address} bytecode does not match the protocol manifest`);
        }
        const identity = legacyJettonIdentity(envelope.account.data ?? "");
        if (
          identity.factoryAddress !== legacy.factoryAddress ||
          identity.jettonMaster !== legacy.jettonMaster ||
          identity.denomination !== BigInt(legacy.denomination)
        ) throw new Error("Pinned legacy Pool storage does not match the protocol manifest");
        const state = await Pool.readState(client, legacy.address);
        if (state.denomination !== identity.denomination) {
          throw new Error("Pinned legacy Pool denomination changed during validation");
        }
        const activeDeposits = state.nextIndex - state.withdrawalCount;
        if (envelope.privacySet < activeDeposits || envelope.privacySet > state.nextIndex) {
          throw new Error(`Pool ${legacy.address} returned invalid privacy metrics`);
        }
        const meta = envelope.metadata;
        const symbol = meta?.symbol ?? legacy.jettonMaster.slice(0, 6);
        return {
          id: legacy.address,
          kind: legacy.kind,
          address: legacy.address,
          symbol,
          name: meta?.name ?? symbol,
          denomination: state.denomination,
          decimals: meta?.decimals ?? 0,
          decimalsVerified: meta?.decimalsVerified === true,
          image: meta?.image ?? null,
          jettonMaster: legacy.jettonMaster,
          nextIndex: state.nextIndex,
          withdrawalCount: state.withdrawalCount,
          privacySet: envelope.privacySet,
          estimatedTvlNanoGram: envelope.estimatedTvlNanoGram === null
            ? null
            : BigInt(envelope.estimatedTvlNanoGram),
          capacity: this.manifest.treeCapacity,
          currentRoot: state.currentRoot,
          ready: state.jettonWallet !== null,
        };
      }));
      const pools = [...canonicalPools, ...legacyPools];
      if (
        new Set(pools.map((pool) => canonicalAddress(pool.address))).size !== pools.length ||
        pools.length !== accountKinds.size
      ) throw new Error("Catalog contains an unauthorized or duplicate Pool");
      this.state = { status: "ready", pools, observedAt: catalog.view.observedAt };
    } catch (error) {
      this.state = {
        status: "error",
        pools: this.state.pools,
        error: error instanceof Error ? error.message : String(error),
      };
    }
    this.emit();
  }
}

export type DepositState =
  | { status: "idle" }
  | { status: "working"; phase: string }
  | { status: "prepared"; pool: ProtocolPool; note: string; walletAddress: string; confirmed: boolean }
  | { status: "submitted"; pool: ProtocolPool; note: string; boc?: string }
  | { status: "error"; error: string };

interface PreparedDepositMemory {
  pool: ProtocolPool;
  walletAddress: string;
  prep: DepositPrep;
  poseidon2: Poseidon2;
  session: PoolSession;
}

const PHASE_LABELS: Record<DepositPhase | WithdrawPhase, string> = {
  "reading-pool": "Reading Pool state",
  "syncing-state": "Verifying Merkle state",
  "computing-witness": "Computing witness",
  "generating-proof": "Generating proof",
  "building-transaction": "Building transaction",
};

export class DepositController extends HeadlessController<DepositState> {
  private state: DepositState = { status: "idle" };
  private prepared: PreparedDepositMemory | null = null;
  private operation = 0;

  constructor(
    private readonly api: ProtocolApi,
    private readonly sessions: PoolSessionFactory,
    private readonly artifacts: ArtifactProvider,
    private readonly wallet: WalletBroadcaster,
  ) {
    super();
  }

  getSnapshot = (): DepositState => this.state;

  async prepare(pool: ProtocolPool, walletAddress: string): Promise<void> {
    const operation = ++this.operation;
    this.prepared = null;
    this.setWorking("Verifying Pool state");
    try {
      if (!pool.ready) throw new Error("This Pool is not ready for deposits");
      const normalizedWallet = Address.parse(walletAddress).toString({ bounceable: true, urlSafe: true });
      const poseidon2 = await this.artifacts.poseidon2();
      if (operation !== this.operation) return;
      const session = await this.sessions.open(pool, poseidon2);
      if (operation !== this.operation) return;
      const userJettonWallet = pool.kind === "jetton"
        ? await this.api.jettonWalletAddress(pool.jettonMaster ?? "", normalizedWallet)
        : undefined;
      if (operation !== this.operation) return;
      const prep = await prepareDeposit(session.client, {
        kind: pool.kind,
        poolAddress: pool.address,
        asset: pool.symbol,
        denomination: pool.denomination,
        userAddress: normalizedWallet,
        ...(userJettonWallet === undefined ? {} : { userJettonWallet }),
        stateProvider: session.stateProvider,
      });
      if (operation !== this.operation) return;
      this.prepared = { pool, walletAddress: normalizedWallet, prep, poseidon2, session };
      this.state = {
        status: "prepared",
        pool,
        note: prep.noteString,
        walletAddress: normalizedWallet,
        confirmed: false,
      };
    } catch (error) {
      if (operation !== this.operation) return;
      this.state = { status: "error", error: error instanceof Error ? error.message : String(error) };
    }
    this.emit();
  }

  confirmSaved(confirmed: boolean): void {
    if (this.state.status !== "prepared") return;
    this.state = { ...this.state, confirmed };
    this.emit();
  }

  async submit(): Promise<void> {
    const prepared = this.prepared;
    if (prepared === null || this.state.status !== "prepared" || !this.state.confirmed) {
      this.state = { status: "error", error: "Save and confirm the note before depositing" };
      this.emit();
      return;
    }
    const operation = ++this.operation;
    try {
      const current = this.wallet.currentAddress();
      if (current === null || !Address.parse(current).equals(Address.parse(prepared.walletAddress))) {
        throw new Error("The connected wallet changed after the note was generated");
      }
      await prepared.session.client.openView();
      if (operation !== this.operation) return;
      this.setWorking("Generating deposit proof");
      const plan = await finalizeDeposit(prepared.session.client, {
        prep: prepared.prep,
        poseidon2: prepared.poseidon2,
        insertProver: this.artifacts.insertProver,
        onProgress: (phase) => {
          if (operation === this.operation) this.setWorking(PHASE_LABELS[phase]);
        },
      });
      if (operation !== this.operation) return;
      this.setWorking("Waiting for wallet approval");
      const result = await this.wallet.send(plan.message);
      if (operation !== this.operation) return;
      this.state = {
        status: "submitted",
        pool: prepared.pool,
        note: prepared.prep.noteString,
        ...(result.boc === undefined ? {} : { boc: result.boc }),
      };
      this.prepared = null;
    } catch (error) {
      if (operation !== this.operation) return;
      this.state = { status: "error", error: error instanceof Error ? error.message : String(error) };
    }
    this.emit();
  }

  reset(): void {
    this.operation += 1;
    this.prepared = null;
    this.state = { status: "idle" };
    this.emit();
  }

  private setWorking(phase: string): void {
    this.state = { status: "working", phase };
    this.emit();
  }
}

export const CREATE_POOL_FEES = {
  jettonMinimum: MIN_CREATE_JETTON_POOL_FEE,
  tonMinimum: MIN_CREATE_TON_POOL_FEE,
  defaultValue: MIN_CREATE_TON_POOL_FEE + 100_000_000n,
} as const;

export const ALLOWED_TON_POOL_DENOMINATIONS = TON_POOL_DENOMINATIONS;

export type CreatePoolState =
  | { status: "idle" }
  | { status: "working"; phase: "resolving-token" | "waiting-wallet" }
  | { status: "submitted"; kind: PoolKind; denomination: bigint; boc?: string }
  | { status: "error"; error: string };

export class CreatePoolController extends HeadlessController<CreatePoolState> {
  private state: CreatePoolState = { status: "idle" };

  constructor(
    private readonly catalog: ProtocolCatalogController,
    private readonly api: ProtocolApi,
    private readonly manifest: ProtocolManifest,
    private readonly wallet: WalletBroadcaster,
  ) {
    super();
  }

  getSnapshot = (): CreatePoolState => this.state;

  async createTon(denomination: bigint): Promise<void> {
    await this.submit("ton", denomination, null);
  }

  async resolveJetton(jettonMaster: string): Promise<{ master: string; metadata: JettonMetadata }> {
    let master: string;
    try {
      master = Address.parse(jettonMaster).toString({ bounceable: true, urlSafe: true });
    } catch {
      throw new Error("Enter a valid Jetton master address");
    }
    const [metadata] = await Promise.all([
      this.api.jettonMetadata(master),
      this.api.jettonWalletAddress(master, this.manifest.factoryAddress).catch(() => {
        throw new Error("This Jetton does not expose a working wallet address getter");
      }),
    ]);
    if (!metadata.decimalsVerified) {
      throw new Error("This Jetton does not publish verifiable decimals on-chain");
    }
    return { master, metadata };
  }

  async createJetton(jettonMaster: string, amount: string): Promise<void> {
    this.state = { status: "working", phase: "resolving-token" };
    this.emit();
    try {
      const resolved = await this.resolveJetton(jettonMaster);
      await this.submit("jetton", parseUnits(amount, resolved.metadata.decimals), resolved.master);
    } catch (error) {
      this.state = { status: "error", error: error instanceof Error ? error.message : String(error) };
      this.emit();
    }
  }

  reset(): void {
    this.state = { status: "idle" };
    this.emit();
  }

  private async submit(kind: PoolKind, denomination: bigint, jettonMaster: string | null): Promise<void> {
    this.state = { status: "working", phase: "waiting-wallet" };
    this.emit();
    try {
      if (this.wallet.currentAddress() === null) throw new Error("Connect a TON mainnet wallet");
      if (denomination <= 0n) throw new Error("Pool denomination must be greater than zero");
      if (kind === "ton" && !TON_POOL_DENOMINATIONS.includes(denomination as typeof TON_POOL_DENOMINATIONS[number])) {
        throw new Error("GRAM Pool denomination is not allowed by the protocol");
      }
      const normalizedMaster = jettonMaster === null
        ? null
        : Address.parse(jettonMaster).toString({ bounceable: true, urlSafe: true });
      const catalog = this.catalog.getSnapshot();
      if (catalog.status !== "ready") throw new Error("Refresh the signed Pool catalog before creating a Pool");
      const legacyAddresses = new Set(
        this.manifest.legacyPools.map((pool) => canonicalAddress(pool.address)),
      );
      const duplicate = catalog.pools.some((pool) =>
        !legacyAddresses.has(canonicalAddress(pool.address)) &&
        pool.kind === kind &&
        pool.denomination === denomination &&
        (kind === "ton" || (
          pool.jettonMaster !== null &&
          normalizedMaster !== null &&
          Address.parse(pool.jettonMaster).equals(Address.parse(normalizedMaster))
        )));
      if (duplicate) throw new Error("This Pool is already registered");
      const message = kind === "ton"
        ? buildCreateTonPool({ factoryAddress: this.manifest.factoryAddress, denomination })
        : buildCreatePool({
            factoryAddress: this.manifest.factoryAddress,
            jettonMaster: normalizedMaster ?? "",
            denomination,
          });
      const result = await this.wallet.send(message);
      this.state = {
        status: "submitted",
        kind,
        denomination,
        ...(result.boc === undefined ? {} : { boc: result.boc }),
      };
    } catch (error) {
      this.state = { status: "error", error: error instanceof Error ? error.message : String(error) };
    }
    this.emit();
  }
}

export type WithdrawState =
  | { status: "idle" }
  | { status: "working"; phase: string }
  | { status: "prepared"; pool: ProtocolPool; recipient: string; relayError?: string }
  | { status: "submitted"; pool: ProtocolPool; recipient: string; via: "relayer" | "wallet"; confirmed: boolean; jobId?: string; boc?: string }
  | { status: "error"; error: string };

export class WithdrawController extends HeadlessController<WithdrawState> {
  private state: WithdrawState = { status: "idle" };
  private plan: {
    pool: ProtocolPool;
    recipient: string;
    plan: WithdrawPlan;
    session: PoolSession;
    proof?: Groth16Proof;
  } | null = null;
  private relayWatchGeneration = 0;
  private operation = 0;

  constructor(
    private readonly api: ProtocolApi,
    private readonly sessions: PoolSessionFactory,
    private readonly artifacts: ArtifactProvider,
    private readonly wallet: WalletBroadcaster,
  ) {
    super();
  }

  getSnapshot = (): WithdrawState => this.state;

  async prepare(noteString: string, recipient: string, pools: readonly ProtocolPool[]): Promise<void> {
    const operation = ++this.operation;
    this.plan = null;
    this.setWorking("Validating note");
    try {
      const note = parseNote(noteString.trim());
      if (note === null) throw new Error("Invalid ZKResistor note");
      const pool = pools.find((candidate) =>
        candidate.kind === note.poolKind &&
        candidate.denomination === note.denominationUnits &&
        Address.parse(candidate.address).equals(Address.parse(note.poolAddress)));
      if (pool === undefined) throw new Error("The note does not belong to a registered Pool");
      const normalizedRecipient = Address.parse(recipient).toString({ bounceable: true, urlSafe: true });
      const poseidon2 = await this.artifacts.poseidon2();
      if (operation !== this.operation) return;
      const session = await this.sessions.open(pool, poseidon2);
      if (operation !== this.operation) return;
      let proof: Groth16Proof | undefined;
      const plan = await buildWithdraw(session.client, {
        kind: pool.kind,
        note,
        poolAddress: pool.address,
        recipientAddress: normalizedRecipient,
        stateProvider: session.stateProvider,
        poseidon2,
        withdrawProver: async (witness) => {
          const result = await this.artifacts.withdrawProver(witness);
          proof = result.proof;
          return result;
        },
        onProgress: (phase) => {
          if (operation === this.operation) this.setWorking(PHASE_LABELS[phase]);
        },
      });
      if (operation !== this.operation) return;
      if (pool.kind === "ton" && proof === undefined) throw new Error("Withdrawal prover returned no proof");
      this.plan = {
        pool,
        recipient: normalizedRecipient,
        plan,
        session,
        ...(pool.kind === "ton" && proof !== undefined ? { proof } : {}),
      };
      this.state = { status: "prepared", pool, recipient: normalizedRecipient };
    } catch (error) {
      if (operation !== this.operation) return;
      this.state = { status: "error", error: error instanceof Error ? error.message : String(error) };
    }
    this.emit();
  }

  async submit(): Promise<void> {
    if (this.plan === null) return;
    const prepared = this.plan;
    const operation = ++this.operation;
    try {
      if (!prepared.plan.relaySafe) throw new Error("This withdrawal proof cannot be shared with a relayer");
      this.setWorking("Submitting withdrawal");
      const expected = {
        poolAddress: prepared.pool.address,
        nullifierHash: prepared.plan.nullifierHash.toString(16).padStart(64, "0"),
        recipient: prepared.recipient,
      };
      const result = await this.api.relayWithdrawal({
        ...expected,
        payloadBoc: prepared.plan.message.payload.toBoc().toString("base64"),
        ...(prepared.pool.kind === "ton" && prepared.proof !== undefined ? { proof: prepared.proof } : {}),
      });
      if (operation !== this.operation) return;
      if (result.status === "failed") throw new Error("Relayer rejected the withdrawal");
      this.state = {
        status: "submitted",
        pool: prepared.pool,
        recipient: prepared.recipient,
        via: "relayer",
        confirmed: false,
        jobId: result.jobId,
      };
      this.plan = null;
      const generation = ++this.relayWatchGeneration;
      void this.watchRelay(result.jobId, expected, prepared.session, prepared.plan.nullifierHash, generation);
    } catch (error) {
      if (operation !== this.operation) return;
      this.state = {
        status: "prepared",
        pool: prepared.pool,
        recipient: prepared.recipient,
        relayError: error instanceof Error ? error.message : String(error),
      };
    }
    this.emit();
  }

  async submitWithWallet(): Promise<void> {
    if (this.plan === null) return;
    const prepared = this.plan;
    const operation = ++this.operation;
    try {
      this.setWorking("Waiting for wallet approval");
      const result = await this.wallet.send(prepared.plan.message);
      if (operation !== this.operation) return;
      this.state = {
        status: "submitted",
        pool: prepared.pool,
        recipient: prepared.recipient,
        via: "wallet",
        confirmed: false,
        ...(result.boc === undefined ? {} : { boc: result.boc }),
      };
      this.plan = null;
    } catch (error) {
      if (operation !== this.operation) return;
      this.state = {
        status: "prepared",
        pool: prepared.pool,
        recipient: prepared.recipient,
        relayError: error instanceof Error ? error.message : String(error),
      };
    }
    this.emit();
  }

  reset(): void {
    this.operation += 1;
    this.relayWatchGeneration += 1;
    this.plan = null;
    this.state = { status: "idle" };
    this.emit();
  }

  private setWorking(phase: string): void {
    this.state = { status: "working", phase };
    this.emit();
  }

  private async watchRelay(
    jobId: string,
    expected: { poolAddress: string; nullifierHash: string; recipient: string },
    session: PoolSession,
    nullifierHash: bigint,
    generation: number,
  ): Promise<void> {
    for (let attempt = 0; attempt < 24; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 5_000));
      if (generation !== this.relayWatchGeneration) return;
      let result;
      try {
        result = await this.api.relayWithdrawalStatus(jobId, expected);
      } catch {
        continue;
      }
      if (generation !== this.relayWatchGeneration) return;
      if (result.status === "failed") {
        if (this.state.status === "submitted" && this.state.jobId === jobId) {
          this.state = { status: "error", error: "Relayer rejected the withdrawal" };
          this.emit();
        }
        return;
      }
      let confirmed = false;
      try {
        confirmed = await session.refreshNullifierStatus(nullifierHash);
      } catch {
        continue;
      }
      if (!confirmed || generation !== this.relayWatchGeneration) continue;
      if (this.state.status === "submitted" && this.state.jobId === jobId) {
        this.state = { ...this.state, confirmed: true };
        this.emit();
      }
      return;
    }
  }
}

export function formatUnits(value: bigint, decimals: number): string {
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const fraction = (value % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return fraction.length === 0 ? whole.toString() : `${whole}.${fraction}`;
}

const MAX_COINS = (1n << 120n) - 1n;

export function parseUnits(value: string, decimals: number): bigint {
  if (!Number.isSafeInteger(decimals) || decimals < 0 || decimals > 30) {
    throw new RangeError("Token decimals are invalid");
  }
  const normalized = value.trim();
  const match = /^(0|[1-9][0-9]*)(?:\.([0-9]+))?$/.exec(normalized);
  if (match === null) throw new Error("Enter a valid token amount");
  const fraction = match[2] ?? "";
  if (fraction.length > decimals) {
    throw new Error(`This token supports at most ${decimals} decimal places`);
  }
  const scale = 10n ** BigInt(decimals);
  const units = BigInt(match[1]!) * scale + BigInt((fraction + "0".repeat(decimals)).slice(0, decimals) || "0");
  if (units <= 0n) throw new Error("Pool denomination must be greater than zero");
  if (units > MAX_COINS) throw new Error("Pool denomination is too large");
  return units;
}

export function formatPoolDenomination(pool: Pick<ProtocolPool, "denomination" | "decimals">): string {
  return formatUnits(pool.denomination, pool.decimals);
}

export type { JettonMetadata };
